import React, { useState, useEffect, useCallback } from 'react';

function MedsTab({ user }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [manualMeds, setManualMeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null); // view medicines inside
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prescRes, medsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/Medication/prescriptions/${user.id}`),
        fetch(`${process.env.REACT_APP_API_URL}/api/Medication/today/${user.id}`)
      ]);
      setPrescriptions(await prescRes.json());
      setManualMeds(await medsRes.json());
    } catch { }
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDeletePrescription = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/prescription/${id}`, { method: 'DELETE' });
      setPrescriptions(prev => prev.filter(p => p.id !== id));
      setDeleteConfirm(null);
      if (selectedPrescription?.prescription?.id === id) setSelectedPrescription(null);
    } catch { alert('Cannot delete. Try again.'); }
  };

  const handleDeleteMed = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/${id}`, { method: 'DELETE' });
      setManualMeds(prev => prev.filter(m => m.id !== id));
      setDeleteConfirm(null);
    } catch { alert('Cannot delete. Try again.'); }
  };

  const openPrescription = async (presc) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/prescription/${presc.id}/medicines`);
      const data = await res.json();
      setSelectedPrescription(data);
    } catch { alert('Cannot load medicines.'); }
  };

  // ── Prescription Detail View ──
  if (selectedPrescription) {
    const { prescription, medicines } = selectedPrescription;
    return (
      <div className="px-4 py-4">
        {/* Back header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setSelectedPrescription(null)}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-full transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div className="flex-1">
            <h2 className="text-xl font-black text-[#12172b]">{prescription.name}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{new Date(prescription.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          </div>
          <button onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/Medication/prescription/view/${prescription.id}`, '_blank')}
            className="flex items-center gap-1.5 px-3.5 py-2.5 bg-indigo-50 text-indigo-600 font-bold rounded-2xl text-xs">
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>open_in_new</span>
            View File
          </button>
        </div>

        {/* Medicines list */}
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">{medicines.length} MEDICINE(S) FOUND</p>
        <div className="space-y-3 mb-5">
          {medicines.map(med => (
            <div key={med.id} className={`bg-white rounded-3xl border shadow-sm p-4 flex items-center gap-3 ${med.isActive === false ? 'border-gray-100 opacity-60' : 'border-gray-100'}`}>
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${med.isActive === false ? 'bg-gray-100' : 'bg-indigo-50'}`}>
                <span className="material-symbols-outlined" style={{ fontSize: '22px', fontVariationSettings: '"FILL" 1', color: med.isActive === false ? '#9ca3af' : '#4f46e5' }}>pill</span>
              </div>
              <div className="flex-1">
                <p className="font-black text-[#12172b] text-sm">{med.medicineName}</p>
                {med.dosage && <p className="text-xs text-gray-500 mt-0.5">{med.dosage}</p>}
                <div className="flex gap-1.5 mt-1 items-center">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${med.isActive === false ? 'bg-gray-100 text-gray-400' : 'bg-emerald-50 text-emerald-600'}`}>
                    {med.isActive === false ? '⏸ Inactive' : '✅ Active'}
                  </span>
                  {med.uploadedBy === 'doctor' && <span className="text-xs font-semibold px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">👨‍⚕️ Doctor</span>}
                </div>
              </div>
              {/* Toggle */}
              <button onClick={async () => {
                try {
                  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/toggle-active/${med.id}`, { method: 'PATCH' });
                  const data = await res.json();
                  setSelectedPrescription(prev => ({
                    ...prev,
                    medicines: prev.medicines.map(m => m.id === med.id ? { ...m, isActive: data.isActive } : m)
                  }));
                } catch { }
              }} className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${med.isActive === false ? 'bg-gray-200' : 'bg-indigo-600'}`}>
                <div className={`w-4 h-4 bg-white rounded-full shadow absolute top-0.5 transition-all ${med.isActive === false ? 'left-0.5' : 'left-5'}`}></div>
              </button>
            </div>
          ))}
          {medicines.length === 0 && (
            <div className="bg-white rounded-3xl p-6 text-center border border-gray-100">
              <p className="text-gray-400 text-sm">No medicines extracted from this prescription.</p>
            </div>
          )}
        </div>

        {/* Delete prescription */}
        <button onClick={() => setDeleteConfirm({ type: 'prescription', id: prescription.id, name: prescription.name })}
          className="w-full py-3.5 border-2 border-red-200 text-red-500 font-bold rounded-2xl text-sm flex items-center justify-center gap-2">
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
          Delete This Prescription
        </button>

        {/* Delete confirm modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
              <p className="font-black text-[#12172b] text-lg mb-1">Delete Prescription?</p>
              <p className="text-gray-500 text-sm mb-4">"{deleteConfirm.name}" and all its medicines will be deleted.</p>
              <div className="flex gap-2">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
                <button onClick={() => handleDeletePrescription(deleteConfirm.id)} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-2xl text-sm">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main Meds Tab ──
  return (
    <div className="px-4 py-4">
      {uploadModal && (
        <PrescriptionUploadModal user={user} onClose={() => setUploadModal(false)} onDone={() => { setUploadModal(false); fetchAll(); }} />
      )}
      {showAddModal && (
        <AddMedModal user={user} onClose={() => { setShowAddModal(false); fetchAll(); }} />
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
            <p className="font-black text-[#12172b] text-lg mb-1">
              {deleteConfirm.type === 'prescription' ? 'Delete Prescription?' : 'Remove Medication?'}
            </p>
            <p className="text-gray-500 text-sm mb-4">"{deleteConfirm.name}" will be permanently deleted.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={() => deleteConfirm.type === 'prescription' ? handleDeletePrescription(deleteConfirm.id) : handleDeleteMed(deleteConfirm.id)}
                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-2xl text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-3xl font-black text-[#12172b]">Medications</h2>
          <p className="text-sm text-gray-400 mt-1">{prescriptions.length} prescription(s) • {manualMeds.length} manual</p>
        </div>
        <div className="flex gap-2.5 shrink-0">
          <button onClick={() => setUploadModal(true)}
            className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center active:scale-95 transition-transform" title="Scan Prescription">
            <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '22px' }}>document_scanner</span>
          </button>
          <button onClick={() => setShowAddModal(true)}
            className="w-12 h-12 rounded-full flex items-center justify-center active:scale-95 transition-transform"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', boxShadow: '0 8px 20px rgba(79,70,229,0.35)' }}
            title="Add Manually">
            <span className="material-symbols-outlined text-white" style={{ fontSize: '24px' }}>add</span>
          </button>
        </div>
      </div>

      {loading && <p className="text-center text-gray-400 text-sm py-8">Loading...</p>}

      {/* Prescription File Cards */}
      {prescriptions.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Prescription Files</p>
          <div className="space-y-3">
            {prescriptions.map(presc => (
              <div key={presc.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => openPrescription(presc)}
                  onKeyDown={e => e.key === 'Enter' && openPrescription(presc)}
                  className="w-full p-4 flex items-center gap-4 text-left active:bg-gray-50 cursor-pointer"
                >
                  <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '28px', fontVariationSettings: '"FILL" 1' }}>post_add</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-[#12172b] text-lg truncate">{presc.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="material-symbols-outlined text-indigo-400" style={{ fontSize: '15px' }}>calendar_today</span>
                      <p className="text-sm text-gray-400">{new Date(presc.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full mt-2">
                      <span className="material-symbols-outlined" style={{ fontSize: '15px', fontVariationSettings: '"FILL" 1', color: '#ec4899' }}>medication</span>
                      {presc.medicineCount} medicine(s)
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <span className="material-symbols-outlined text-gray-300" style={{ fontSize: '24px' }}>chevron_right</span>
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm({ type: 'prescription', id: presc.id, name: presc.name }); }}
                      className="w-9 h-9 flex items-center justify-center text-red-400 bg-red-50 hover:bg-red-100 rounded-full transition-colors">
                      <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Medications */}
      {manualMeds.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Manually Added</p>
          <div className="space-y-3">
            {manualMeds.map(med => (
              <div key={med.id} className={`bg-white rounded-3xl border shadow-sm p-4 flex items-center gap-3 ${med.isActive === false ? 'border-gray-100 opacity-60' : 'border-gray-100'}`}>
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${med.isActive === false ? 'bg-gray-100' : 'bg-indigo-50'}`}>
                  <span className="material-symbols-outlined" style={{ fontSize: '22px', fontVariationSettings: '"FILL" 1', color: med.isActive === false ? '#9ca3af' : '#4f46e5' }}>pill</span>
                </div>
                <div className="flex-1">
                  <p className="font-black text-[#12172b] text-sm">{med.medicineName}</p>
                  {med.dosage && <p className="text-xs text-gray-500 mt-0.5">{med.dosage}</p>}
                  <div className="flex gap-1.5 mt-1 items-center">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${med.isActive === false ? 'bg-gray-100 text-gray-400' : 'bg-emerald-50 text-emerald-600'}`}>
                      {med.isActive === false ? '⏸ Inactive' : '✅ Active'}
                    </span>
                    {med.uploadedBy === 'doctor' && <span className="text-xs font-semibold px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">👨‍⚕️ Doctor</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-center">
                  {/* Toggle Button */}
                  <button onClick={async () => {
                    try {
                      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/toggle-active/${med.id}`, { method: 'PATCH' });
                      const data = await res.json();
                      setManualMeds(prev => prev.map(m => m.id === med.id ? { ...m, isActive: data.isActive } : m));
                    } catch { }
                  }} className={`w-10 h-5 rounded-full transition-all relative ${med.isActive === false ? 'bg-gray-200' : 'bg-indigo-600'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow absolute top-0.5 transition-all ${med.isActive === false ? 'left-0.5' : 'left-5'}`}></div>
                  </button>
                  <button onClick={() => setDeleteConfirm({ type: 'med', id: med.id, name: med.medicineName })}
                    className="w-8 h-8 flex items-center justify-center text-red-400 bg-red-50 hover:bg-red-100 rounded-full transition-colors">
                    <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {prescriptions.length === 0 && manualMeds.length === 0 && !loading && (
        <div className="bg-white rounded-3xl px-6 py-10 text-center border border-gray-100 shadow-sm">
          {/* Decorative capsule illustration */}
          <div className="relative w-40 h-40 mx-auto mb-2 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-gray-50" />
            <span className="material-symbols-outlined absolute text-indigo-100" style={{ top: '8px', left: '10px', fontSize: '20px' }}>auto_awesome</span>
            <span className="material-symbols-outlined absolute text-indigo-100" style={{ top: '30px', right: '4px', fontSize: '18px' }}>auto_awesome</span>
            <span className="material-symbols-outlined absolute text-indigo-100" style={{ bottom: '14px', left: '2px', fontSize: '16px' }}>auto_awesome</span>
            <svg width="90" height="90" viewBox="0 0 100 100" className="relative z-10" style={{ filter: 'drop-shadow(0 12px 16px rgba(79,70,229,0.25))' }}>
              <g transform="rotate(45 50 50)">
                <path d="M30 20 H70 A20 20 0 0 1 70 60 H30 A20 20 0 0 1 30 20 Z" fill="url(#pillGrad1)" />
                <path d="M50 20 H70 A20 20 0 0 1 70 60 H50 Z" fill="url(#pillGrad2)" />
              </g>
              <defs>
                <linearGradient id="pillGrad1" x1="30" y1="20" x2="50" y2="60" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#7c3aed" />
                  <stop offset="1" stopColor="#4f46e5" />
                </linearGradient>
                <linearGradient id="pillGrad2" x1="50" y1="20" x2="70" y2="60" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#ec4899" />
                  <stop offset="1" stopColor="#db2777" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <p className="text-[#12172b] font-black text-2xl mb-2">No medications yet</p>
          <p className="text-gray-400 text-base mb-7 leading-snug">Upload a prescription or add medicines<br />manually to get started.</p>

          <div className="flex gap-3 justify-center">
            <button onClick={() => setUploadModal(true)}
              className="flex-1 max-w-[190px] px-4 py-3.5 bg-emerald-50 text-emerald-600 font-bold rounded-full text-sm inline-flex items-center justify-center gap-1 whitespace-nowrap">
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: '16px' }}>document_scanner</span>
              <span>Scan Prescription</span>
            </button>
            <button onClick={() => setShowAddModal(true)}
              className="flex-1 max-w-[190px] px-4 py-3.5 text-white font-bold rounded-full text-sm inline-flex items-center justify-center gap-1 whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', boxShadow: '0 8px 20px rgba(79,70,229,0.3)' }}>
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: '16px' }}>add</span>
              <span>Add Manually</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════
// ADD MED MODAL (bottom sheet)
// ═══════════════════════════════
function AddMedModal({ user, onClose }) {
  const [form, setForm] = useState({ medicineName: '', dosage: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.medicineName.trim()) { setMessage('Medicine name is required.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/add-by-patient`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: user.id, ...form })
      });
      const data = await res.json();
      if (res.ok) { setMessage('✅ Added!'); setTimeout(onClose, 1200); }
      else setMessage(`❌ ${data.message}`);
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-end">
      <div className="w-full bg-white rounded-t-3xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: '#ece7fb' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '30px', color: '#7c3aed' }}>medication_liquid</span>
            </div>
            <div>
              <p className="font-black text-[#12172b] text-2xl">Add Medication</p>
              <p className="text-sm text-gray-400 mt-0.5">Save to your <span className="text-gray-500 font-semibold">medication records</span></p>
            </div>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center text-[#12172b] bg-gray-100 hover:bg-gray-200 rounded-full shrink-0">
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
          </button>
        </div>

        {message && <div className={`p-3 rounded-2xl text-xs mb-4 ${message.includes('✅') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>{message}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Medicine Name */}
          <div>
            <label className="text-base font-bold text-[#12172b] mb-2 block">Medicine Name <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-3 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white focus-within:border-[#7c3aed]">
              <span className="material-symbols-outlined text-gray-400 shrink-0" style={{ fontSize: '20px' }}>pill</span>
              <input type="text" placeholder="e.g. Paracetamol, Metformin..." value={form.medicineName}
                onChange={e => setForm({ ...form, medicineName: e.target.value })}
                className="w-full bg-transparent outline-none border-none text-base text-[#12172b] placeholder-gray-400" />
            </div>
          </div>

          {/* Dosage */}
          <div>
            <label className="text-base font-bold text-[#12172b] mb-2 block">Dosage</label>
            <div className="flex items-center gap-3 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white focus-within:border-[#7c3aed]">
              <span className="material-symbols-outlined text-gray-400 shrink-0" style={{ fontSize: '20px' }}>local_drink</span>
              <input type="text" placeholder="e.g. 500mg, 10ml..." value={form.dosage}
                onChange={e => setForm({ ...form, dosage: e.target.value })}
                className="w-full bg-transparent outline-none border-none text-base text-[#12172b] placeholder-gray-400" />
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full py-4 text-white font-bold rounded-full text-base flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', boxShadow: '0 8px 25px rgba(79,70,229,0.3)' }}>
            {loading ? (
              'Saving...'
            ) : (
              <>
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>medication_liquid</span>
                Save to Records
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════
// PRESCRIPTION UPLOAD MODAL
// ═══════════════════════════════
function PrescriptionUploadModal({ user, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [prescName, setPrescName] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('upload'); // upload | reading | done
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState('');

  const handleUpload = async () => {
    if (!file) { setMessage('Please select a prescription file.'); return; }
    if (!prescName.trim()) { setMessage('Please give this prescription a name.'); return; }
    setLoading(true);
    setStep('reading');
    const formData = new FormData();
    formData.append('PatientId', user.id);
    formData.append('Name', prescName.trim());
    formData.append('File', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Medication/upload-prescription`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) { setMessage(`❌ ${data.message || 'Upload failed.'}`); setStep('upload'); setLoading(false); return; }
      setResult(data);
      setStep('done');
    } catch { setMessage('❌ Cannot connect to server.'); setStep('upload'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-end">
      <div className="w-full bg-white rounded-t-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: '#f1e9fb' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#7c3aed' }}>document_scanner</span>
            </div>
            <div>
              <p className="font-black text-[#12172b] text-2xl">Upload Prescription</p>
              <p className="text-sm text-gray-400 mt-0.5">AI reads &amp; saves medicines</p>
            </div>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center text-[#12172b] bg-gray-100 hover:bg-gray-200 rounded-full shrink-0">
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>close</span>
          </button>
        </div>

        {message && <div className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-xs mb-4">{message}</div>}

        {/* STEP: Upload */}
        {step === 'upload' && (
          <div className="space-y-5">
            <div className="rounded-2xl p-4 flex gap-3 items-start" style={{ background: '#eef0fc', border: '1px solid #dfe2f7' }}>
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: '22px', color: '#7c3aed' }}>auto_awesome</span>
              <p className="text-sm text-[#3b3f52] leading-snug">AI will read your prescription and save all medicines under one file in your Meds tab.</p>
            </div>

            {/* Prescription Name */}
            <div>
              <label className="text-base font-bold text-[#12172b] mb-2 block">Prescription Name <span className="text-red-500">*</span></label>
              <div className="flex items-center gap-3 px-4 py-3.5 border border-gray-200 rounded-2xl bg-white focus-within:border-[#7c3aed]">
                <span className="material-symbols-outlined text-gray-400 shrink-0" style={{ fontSize: '20px' }}>description</span>
                <input type="text" placeholder="e.g. Apollo Visit Jan 2025, Fever Medicines..."
                  value={prescName} onChange={e => setPrescName(e.target.value)}
                  className="w-full bg-transparent outline-none border-none text-base text-[#12172b] placeholder-gray-400" />
              </div>
            </div>

            {/* File Upload — with camera option */}
            <div>
              <label className="text-base font-bold text-[#12172b] mb-2 block">Prescription File <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Camera option */}
                <label className="flex flex-col items-center gap-2.5 rounded-2xl p-5 cursor-pointer active:scale-95 transition-all" style={{ background: '#f5f2fc', border: '1px solid #e6def7' }}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#e6def7' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '26px', color: '#7c3aed', fontVariationSettings: '"FILL" 1' }}>photo_camera</span>
                  </div>
                  <p className="font-extrabold text-[#12172b] text-base">Take Photo</p>
                  <p className="text-sm text-gray-500">Use camera</p>
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
                </label>
                {/* Gallery/files option */}
                <label className="flex flex-col items-center gap-2.5 rounded-2xl p-5 cursor-pointer active:scale-95 transition-all" style={{ background: '#eef2fc', border: '1px solid #d9e3f7' }}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#4f46e5' }}>
                    <span className="material-symbols-outlined text-white" style={{ fontSize: '26px', fontVariationSettings: '"FILL" 1' }}>upload_file</span>
                  </div>
                  <p className="font-extrabold text-[#12172b] text-base">Upload File</p>
                  <p className="text-sm text-gray-500">PDF, JPG, PNG</p>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                    onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
                </label>
              </div>
              {file && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-2xl px-3 py-2">
                  <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '16px', fontVariationSettings: '"FILL" 1' }}>check_circle</span>
                  <p className="text-xs font-semibold text-emerald-700 truncate">{file.name}</p>
                  <button onClick={() => setFile(null)} className="ml-auto text-gray-400 hover:text-red-400">
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                  </button>
                </div>
              )}
            </div>

            <button onClick={handleUpload} disabled={!file || !prescName.trim() || loading}
              className="w-full py-4 text-white font-bold rounded-full text-base flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', boxShadow: '0 8px 25px rgba(79,70,229,0.3)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>smart_toy</span>
              Upload &amp; Let AI Read
            </button>
          </div>
        )}

        {/* STEP: Reading */}
        {step === 'reading' && (
          <div className="flex flex-col items-center py-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '32px', fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
            </div>
            <p className="font-black text-[#12172b] text-lg mb-2">AI is reading...</p>
            <p className="text-gray-400 text-sm">Extracting medicines from "{prescName}"</p>
            <div className="flex gap-1 mt-4">
              {[0, 0.2, 0.4].map((d, i) => (
                <div key={i} className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: `${d}s` }}></div>
              ))}
            </div>
          </div>
        )}

        {/* STEP: Done */}
        {step === 'done' && result && (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '36px', fontVariationSettings: '"FILL" 1' }}>check_circle</span>
              </div>
              <p className="font-black text-[#12172b] text-lg">Prescription Saved!</p>
              <p className="text-gray-400 text-sm mt-1">"{result.prescriptionName}" has been added to your Meds tab</p>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
              <p className="text-xs font-bold text-emerald-700 mb-2">💊 {result.medications?.length || 0} medicine(s) extracted:</p>
              <div className="space-y-1.5">
                {result.medications?.map((med, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full shrink-0"></span>
                    <p className="text-xs text-emerald-800 font-semibold">{med.medicineName} {med.dosage && `— ${med.dosage}`}</p>
                  </div>
                ))}
                {(!result.medications || result.medications.length === 0) && (
                  <p className="text-xs text-emerald-600">No medicines could be extracted. Try a clearer image.</p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setStep('upload'); setFile(null); setPrescName(''); setResult(null); setMessage(''); }}
                className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">
                Upload Another
              </button>
              <button onClick={onDone} className="flex-1 py-3 text-white font-bold rounded-2xl text-sm"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                View in Meds
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MedsTab;